import type { PuntoStorico, RichiestaStorico, Risoluzione } from './DeviceClient';

/**
 * Serie storiche sintetiche e deterministiche: la stessa richiesta restituisce
 * sempre gli stessi punti, senza tenere in memoria 24 mesi di campioni.
 * L'aggregazione avviene "lato server" (specifica §13): il client riceve già
 * la risoluzione chiesta.
 */

export const PASSO_RISOLUZIONE_MS: Record<Risoluzione, number> = {
  '1m': 60_000,
  '15m': 15 * 60_000,
  '1h': 3_600_000,
  '1d': 86_400_000,
};

export interface ProfiloCanale {
  base: number;
  ampiezzaGiornaliera: number;
  /** Ora del picco giornaliero (0–24). */
  oraPicco: number;
  rumore: number;
  /** Valore nullo per l'intero intervallo (sonda assente). */
  assente?: boolean;
  /** Limite inferiore (es. una produzione fotovoltaica non è mai negativa). */
  min?: number;
}

function rumoreDeterministico(seme: number): number {
  const x = Math.sin(seme * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x) - 0.5;
}

export function valoreStorico(profilo: ProfiloCanale, ts: number, indiceCanale: number): number | null {
  if (profilo.assente) return null;
  const giorno = ts / 86_400_000;
  const ora = ((ts / 3_600_000) % 24 + 24) % 24;
  const stagionale = Math.sin((giorno / 365) * Math.PI * 2) * profilo.ampiezzaGiornaliera * 0.3;
  const giornaliero = Math.cos(((ora - profilo.oraPicco) / 24) * Math.PI * 2) * profilo.ampiezzaGiornaliera;
  const rumore = rumoreDeterministico(Math.floor(ts / 60_000) + indiceCanale * 1000) * profilo.rumore;
  const v = Math.round((profilo.base + stagionale + giornaliero + rumore) * 10) / 10;
  return profilo.min === undefined ? v : Math.max(profilo.min, v);
}

export function generaStorico(
  richiesta: RichiestaStorico,
  profili: Record<string, ProfiloCanale>,
  buco?: { da: number; a: number },
): PuntoStorico[] {
  const passo = PASSO_RISOLUZIONE_MS[richiesta.risoluzione];
  const punti: PuntoStorico[] = [];
  const inizio = Math.floor(richiesta.da / passo) * passo;
  for (let ts = inizio; ts <= richiesta.a; ts += passo) {
    const valori: Record<string, number | null> = {};
    richiesta.canali.forEach((canale, i) => {
      const profilo = profili[canale];
      if (!profilo || (buco && ts >= buco.da && ts < buco.a)) {
        valori[canale] = null;
      } else {
        valori[canale] = valoreStorico(profilo, ts, i);
      }
    });
    punti.push({ ts, valori });
  }
  return punti;
}
